import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import type { MarketingCampaignBudgetSegment } from "@common/interfaces/KYDMarketing";
import type { Price } from "@common/interfaces/KYDTicket";
import { scheduleInputToUtcIso } from "@common/utils/adsetSchedule";
import {
  normalizeScheduledBudgetSegmentsToEventDayJunctions,
  snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid,
  validateScheduledBudgetWithinAdFlight,
  type CampaignBudgetWindowsValidationError,
} from "@common/utils/campaignBudgetMeta";
import { minorAmountToPrice } from "@common/utils/minorAmountToPrice";

/** Event-wall phased row from Yup / GuidedBudgetEditor (not persisted UTC ISO). */
export type WallTimedBudgetSegmentLike = {
  time_start?: string | null;
  time_end?: string | null;
  amount?: { amount?: unknown; currency?: string | null } | null;
};

/**
 * Form-only: wall / wire segment times → UTC ISO, then
 * {@link validateScheduledBudgetWithinAdFlight} (same rules as launch / publish).
 */
export function validateScheduledBudgetWallsWithinUtcFlight(params: {
  segments: WallTimedBudgetSegmentLike[];
  eventTimezone: string | undefined;
  flightStartIso: string;
  flightEndIso: string;
}): CampaignBudgetWindowsValidationError | null {
  const tz =
    params.eventTimezone?.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
  const segmentModels: MarketingCampaignBudgetSegment[] =
    params.segments.map((s) => {
      const rawStart =
        scheduleInputToUtcIso(String(s.time_start ?? "").trim(), tz) ??
        String(s.time_start);
      const rawEnd =
        scheduleInputToUtcIso(String(s.time_end ?? "").trim(), tz) ??
        String(s.time_end);
      return {
        time_start: rawStart,
        time_end: rawEnd,
        amount: minorAmountToPrice(
          1,
          (s.amount?.currency ?? "USD") as Price["currency"]
        ),
      };
    });
  const flight = snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid({
    flightStartIso: params.flightStartIso,
    flightEndIso: params.flightEndIso,
  });
  const normalized = normalizeScheduledBudgetSegmentsToEventDayJunctions({
    segments: segmentModels,
    eventIana: tz,
    flightStartIso: flight.flightStartIso,
    flightEndIso: flight.flightEndIso,
  });
  return validateScheduledBudgetWithinAdFlight({
    segments: normalized,
    flightStartIso: flight.flightStartIso,
    flightEndIso: flight.flightEndIso,
  });
}
